/**
 * embeddedSubworkflowUtils.ts — 嵌套 Parallelization 容器补全
 *
 * SDK 将 ParallelizationWorkflow 嵌入 Chaining stage 时，可能只发 stage-start
 *（无 subworkflow-start / parallel-item-start），loop session_id 形如
 * `{child_workflow_id}-{task_key}`。此处按 session_id 补全 subworkflow / item / aggregate 容器。
 */
import type { LangchainAgentEvent } from '../../types/events';
import { buildContainerId } from './workflowContainerProjector';
import { buildLoopSessionId } from './loopSessionUtils';
import {
  hasNativeNestedStructureInTree,
  isNativeNestedStructureEvent,
} from './nativeNestedUtils';
import type {
  WorkflowContainerRuntimeNode,
  WorkflowContainerScope,
  WorkflowContainerTreeState,
} from '../../types/workflowContainer';
import type { WorkflowSessionState } from '../../types/workflowSession';

export interface ParsedLoopSessionParts {
  workflowId: string;
  taskKey: string;
}

/** `{workflow_id}-{task_key}`；task_key 不含连字符（与 SDK WorkflowSessionIdPolicy 一致）。 */
export function parseLoopSessionParts(sessionId: string): ParsedLoopSessionParts | null {
  const trimmed = sessionId.trim();
  const dash = trimmed.lastIndexOf('-');
  if (dash <= 0 || dash >= trimmed.length - 1) return null;
  return {
    workflowId: trimmed.slice(0, dash),
    taskKey: trimmed.slice(dash + 1),
  };
}

export function isEmbeddedChildLoopSession(
  sessionId: string,
  rootWorkflowId: string,
): boolean {
  const parts = parseLoopSessionParts(sessionId);
  if (!parts) return false;
  if (parts.workflowId === rootWorkflowId) return false;
  // 同属根 workflow 家族（qa-review vs qa-review-pipeline）不是嵌套子流。
  if (parts.workflowId.startsWith(`${rootWorkflowId}-`)) return false;
  if (rootWorkflowId.startsWith(`${parts.workflowId}-`)) return false;
  // 嵌套子 workflow id 至少三段（如 demo-review-sub）。
  return parts.workflowId.split('-').length >= 3;
}

function readWorkflowIdFromData(data: Record<string, unknown>): string | undefined {
  const value = data.workflow_id;
  return typeof value === 'string' ? value : undefined;
}

function readStageKeyFromData(data: Record<string, unknown>): string | undefined {
  const value = data.stage_key;
  return typeof value === 'string' ? value : undefined;
}

export function shouldPrebindLoopSessionFromStructureOpen(
  eventType: string,
  eventSessionId: string | null | undefined,
  derivedLoopSessionId: string,
  data: Record<string, unknown> = {},
): boolean {
  if (!eventSessionId) return true;
  if (eventType !== 'stage-start') return true;
  if (eventSessionId === derivedLoopSessionId) return true;

  // 上一 stage 的 loop session_id 泄漏到容器 stage-start（嵌套 parallel_review）。
  const rootWorkflowId = readWorkflowIdFromData(data);
  const stageKey = readStageKeyFromData(data);
  const parsed = parseLoopSessionParts(eventSessionId);
  if (rootWorkflowId && stageKey && parsed) {
    if (parsed.workflowId === rootWorkflowId && parsed.taskKey !== stageKey) {
      return false;
    }
  }
  return true;
}

function resolveRootWorkflow(
  tree: WorkflowContainerTreeState,
): { workflowId: string; workflowPath: string } | null {
  const rootId = tree.rootContainerIds[0];
  if (!rootId) return null;
  const root = tree.containersById[rootId];
  if (!root || root.scope !== 'workflow') return null;
  return {
    workflowId: root.scope_key,
    workflowPath: root.workflow_path,
  };
}

function humanizeTaskTitle(taskKey: string): string {
  if (taskKey === 'aggregate') return 'Merge Perspectives';
  if (taskKey === 'architecture') return 'Architecture Path';
  if (taskKey === 'implementation') return 'Implementation Path';
  return taskKey
    .split('_')
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : part))
    .join(' ');
}

function findEmbeddedHostStage(
  tree: WorkflowContainerTreeState,
  rootWorkflowPath: string,
  childWorkflowId: string,
): WorkflowContainerRuntimeNode | null {
  const childPath = `${rootWorkflowPath}>${childWorkflowId}`;
  const subId = buildContainerId(childPath, 'subworkflow', childWorkflowId);
  const existingSub = tree.containersById[subId];
  if (existingSub?.parent_container_id) {
    return tree.containersById[existingSub.parent_container_id] ?? null;
  }

  const runningStages = tree.activeContainerIds
    .map((id) => tree.containersById[id])
    .filter(
      (node): node is WorkflowContainerRuntimeNode =>
        node != null
        && node.scope === 'stage'
        && node.workflow_path === rootWorkflowPath
        && node.is_open,
    );

  if (runningStages.length === 0) return null;

  return runningStages.reduce((best, node) =>
    (node.display?.progress_current ?? 0) >= (best.display?.progress_current ?? 0)
      ? node
      : best,
  );
}

function upsertRuntimeNode(
  tree: WorkflowContainerTreeState,
  node: WorkflowContainerRuntimeNode,
): WorkflowContainerTreeState {
  const activeContainerIds = tree.activeContainerIds.includes(node.container_id)
    ? tree.activeContainerIds
    : node.is_open
      ? [...tree.activeContainerIds, node.container_id]
      : tree.activeContainerIds;

  return {
    ...tree,
    activeContainerIds,
    containersById: {
      ...tree.containersById,
      [node.container_id]: node,
    },
  };
}

function completeOpenItemsUnderSubworkflow(
  tree: WorkflowContainerTreeState,
  subworkflowId: string,
): WorkflowContainerTreeState {
  let next = tree;
  for (const node of Object.values(tree.containersById)) {
    if (
      node.parent_container_id === subworkflowId
      && node.scope === 'item'
      && node.status === 'running'
    ) {
      next = upsertRuntimeNode(next, {
        ...node,
        status: 'completed',
        is_open: false,
      });
    }
  }
  const activeContainerIds = next.activeContainerIds.filter(
    (id) => next.containersById[id]?.is_open,
  );
  return { ...next, activeContainerIds };
}

function resolveLeafScope(taskKey: string): WorkflowContainerScope {
  return taskKey === 'aggregate' ? 'aggregate' : 'item';
}

/**
 * 是否应对当前会话启用 embedded 子流合成（D29 方案 B：native 优先）。
 * SDK ≥1.6.6 已正确发 native subworkflow 结构事件；embedded 路径仅服务旧 fixture / legacy replay。
 * 传入 pendingEvent 可在首条 native 结构事件到达时立即跳过合成。
 */
export function shouldUseEmbeddedSynthesis(
  treeOrState: WorkflowContainerTreeState | WorkflowSessionState,
  pendingEvent?: LangchainAgentEvent,
): boolean {
  const tree = 'containerTree' in treeOrState ? treeOrState.containerTree : treeOrState;
  if (pendingEvent && isNativeNestedStructureEvent(pendingEvent)) {
    return false;
  }
  if (hasNativeNestedStructureInTree(tree)) {
    return false;
  }
  return true;
}

/** 为嵌套子流补全 subworkflow / item|aggregate 容器并绑定 loopSessionId。 */
export function ensureEmbeddedSubworkflowContainers(
  tree: WorkflowContainerTreeState,
  loopSessionId: string,
): WorkflowContainerTreeState {
  if (!shouldUseEmbeddedSynthesis(tree)) {
    return tree;
  }

  const root = resolveRootWorkflow(tree);
  const parts = parseLoopSessionParts(loopSessionId);
  if (!root || !parts || !isEmbeddedChildLoopSession(loopSessionId, root.workflowId)) {
    return tree;
  }

  const hostStage = findEmbeddedHostStage(tree, root.workflowPath, parts.workflowId);
  if (!hostStage) return tree;

  let next = tree;

  if (hostStage.loopSessionId) {
    next = upsertRuntimeNode(next, { ...hostStage, loopSessionId: null });
  }

  const childPath = `${root.workflowPath}>${parts.workflowId}`;
  const subId = buildContainerId(childPath, 'subworkflow', parts.workflowId);
  let subNode = next.containersById[subId];
  if (!subNode) {
    subNode = {
      container_id: subId,
      scope: 'subworkflow',
      workflow_path: childPath,
      workflow_depth: hostStage.workflow_depth + 1,
      scope_key: parts.workflowId,
      title: 'Dual-Perspective Review',
      subtitle: 'Nested parallel sub-workflow',
      status: 'running',
      pattern: 'parallelization',
      parent_container_id: hostStage.container_id,
      display: {
        title: 'Dual-Perspective Review',
        description: 'Nested parallel sub-workflow',
        pattern: 'parallelization',
        status: 'running',
      },
      content_blocks: [],
      is_open: true,
      loopSessionId: null,
    };
    next = upsertRuntimeNode(next, subNode);
  }

  const leafScope = resolveLeafScope(parts.taskKey);
  const leafId = buildContainerId(childPath, leafScope, parts.taskKey);
  let leafNode = next.containersById[leafId];
  if (!leafNode) {
    leafNode = {
      container_id: leafId,
      scope: leafScope,
      workflow_path: childPath,
      workflow_depth: hostStage.workflow_depth + 1,
      scope_key: parts.taskKey,
      title: humanizeTaskTitle(parts.taskKey),
      subtitle: '',
      status: 'running',
      pattern: 'parallelization',
      parent_container_id: subId,
      display: {
        title: humanizeTaskTitle(parts.taskKey),
        pattern: 'parallelization',
        status: 'running',
      },
      content_blocks: [],
      is_open: true,
      loopSessionId: null,
    };
    next = upsertRuntimeNode(next, leafNode);
  }

  if (leafScope === 'aggregate') {
    next = completeOpenItemsUnderSubworkflow(next, subId);
  }

  if (leafNode.loopSessionId !== loopSessionId) {
    next = upsertRuntimeNode(next, {
      ...leafNode,
      loopSessionId,
      content_blocks: [],
    });
  }

  return next;
}

export function findContainerIdByLoopSession(
  tree: WorkflowContainerTreeState,
  loopSessionId: string,
): string | null {
  for (const [containerId, node] of Object.entries(tree.containersById)) {
    if (node.loopSessionId === loopSessionId) return containerId;
  }
  return null;
}

const PARALLEL_ITEM_STRUCTURE_EVENTS = new Set([
  'parallel-item-start',
  'parallel-item-done',
  'parallel-item-failed',
]);

function readItemKey(data: Record<string, unknown>): string | undefined {
  const value = data.item_key;
  return typeof value === 'string' ? value : undefined;
}

/**
 * nested parallel item：structure workflow_id（如 parallel_review）与 SDK session 前缀
 *（如 demo-review-sub）不一致时，以 session_id 的 task_key 与 item_key 对齐为准。
 */
function resolveParallelItemLoopSessionId(
  eventType: string,
  eventSessionId: string | null | undefined,
  derivedLoopSessionId: string,
  data: Record<string, unknown>,
): string | null {
  if (!PARALLEL_ITEM_STRUCTURE_EVENTS.has(eventType) || !eventSessionId) return null;
  if (eventSessionId === derivedLoopSessionId) return null;

  const itemKey = readItemKey(data);
  const parsed = parseLoopSessionParts(eventSessionId);
  if (itemKey && parsed?.taskKey === itemKey) {
    return eventSessionId;
  }
  return null;
}

/** structure open/close 预绑定用的 loopSessionId（aggregate 可能带 child workflow 前缀）。 */
export function resolveStructureOpenLoopSessionId(
  eventType: string,
  eventSessionId: string | null | undefined,
  derivedLoopSessionId: string,
  data: Record<string, unknown>,
): string {
  const rootWorkflowId = readWorkflowId(data) ?? '';
  if (
    (eventType === 'parallel-aggregate-start'
      || eventType === 'parallel-aggregate-done'
      || eventType === 'parallel-aggregate-failed')
    && eventSessionId
    && isEmbeddedChildLoopSession(eventSessionId, rootWorkflowId)
  ) {
    return eventSessionId;
  }

  const parallelItemSessionId = resolveParallelItemLoopSessionId(
    eventType,
    eventSessionId,
    derivedLoopSessionId,
    data,
  );
  if (parallelItemSessionId) {
    return parallelItemSessionId;
  }

  return derivedLoopSessionId;
}

const EMBEDDED_AGGREGATE_STRUCTURE_EVENTS = new Set([
  'parallel-aggregate-start',
  'parallel-aggregate-done',
  'parallel-aggregate-failed',
]);

/** 嵌套 aggregate 结构事件的 workflow_path 落到子流路径，供 projector 开/关正确容器。 */
export function patchEmbeddedStructureEvent(
  event: LangchainAgentEvent,
  rootWorkflowId: string,
): LangchainAgentEvent {
  if (!EMBEDDED_AGGREGATE_STRUCTURE_EVENTS.has(event.event_type)) {
    return event;
  }
  if (!event.session_id || !isEmbeddedChildLoopSession(event.session_id, rootWorkflowId)) {
    return event;
  }

  const parts = parseLoopSessionParts(event.session_id);
  if (!parts) return event;

  const data = (event.data ?? {}) as Record<string, unknown>;
  const rootPath = readString(data, 'workflow_path') ?? rootWorkflowId;
  const childPath = `${rootPath}>${parts.workflowId}`;
  const workflowDepth =
    typeof data.workflow_depth === 'number' ? data.workflow_depth + 1 : 1;

  return {
    ...event,
    data: {
      ...data,
      workflow_path: childPath,
      workflow_depth: workflowDepth,
    },
  };
}

function readString(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

function isDescendantOf(
  containersById: Record<string, WorkflowContainerRuntimeNode>,
  ancestorId: string,
  nodeId: string,
): boolean {
  const seen = new Set<string>();
  let current = containersById[nodeId]?.parent_container_id ?? null;
  while (current) {
    if (current === ancestorId) return true;
    if (seen.has(current)) return false;
    seen.add(current);
    current = containersById[current]?.parent_container_id ?? null;
  }
  return false;
}

/** stage-done 时收口其下嵌套子流容器（避免 item/aggregate 仍 running + Spinner）。 */
export function settleCompletedStageSubtree(
  tree: WorkflowContainerTreeState,
  stageContainerId: string,
): WorkflowContainerTreeState {
  let next = tree;
  for (const node of Object.values(tree.containersById)) {
    if (
      node.container_id !== stageContainerId
      && !isDescendantOf(tree.containersById, stageContainerId, node.container_id)
    ) {
      continue;
    }
    if (node.container_id === stageContainerId) continue;
    if (node.status === 'completed' || node.status === 'failed') continue;
    // stage-done 若早于 sibling agent 到达，不得把仍 active 的后代强行 settle
    if (
      node.status === 'running'
      || node.status === 'retrying'
      || node.status === 'pending'
      || node.status === 'blocked'
    ) {
      continue;
    }
    next = upsertRuntimeNode(next, {
      ...node,
      status: 'completed',
      is_open: false,
    });
  }
  const activeContainerIds = next.activeContainerIds.filter(
    (id) => next.containersById[id]?.is_open,
  );
  return { ...next, activeContainerIds };
}

function readWorkflowId(data: Record<string, unknown>): string | undefined {
  return readWorkflowIdFromData(data);
}

export { buildLoopSessionId };
